import { useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { Button, IconButton, TextField, Badge, Panel, PageHead, EmptyState } from "@/sentinel";

/** Multi-line field in the TextField style (the library has no textarea). */
function TextArea({ label, rows = 3, ...rest }) {
  const id = useId();
  return (
    <div className="sn-field">
      {label && <label className="sn-field-label" htmlFor={id}>{label}</label>}
      <div className="sn-input-wrap" style={{ height: "auto", alignItems: "stretch" }}>
        <textarea id={id} rows={rows} className="sn-input" style={{ padding: "var(--space-2)", lineHeight: "20px", resize: "vertical" }} {...rest} />
      </div>
    </div>
  );
}

function PromptCard({ p, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ ref: p.ref, title: p.title, body: p.body });
  const save = async () => {
    try { await api.pmUpdatePrompt(p.id, f); setEditing(false); onSaved(); toast.success("Prompt saved."); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  return (
    <Panel
      title={p.title}
      icon="FileText"
      data-testid={`pm-prompt-${p.id}`}
      meta={
        <span className="sn-row" style={{ gap: "var(--space-1)" }}>
          <Badge className="sn-num" icon="Hash">{p.ref}</Badge>
          {p.private_to?.length > 0 && (
            <Badge icon="Lock" title={`Visible only to ${p.private_to.join(", ")}`} data-testid={`pm-prompt-private-${p.ref}`}>PRIVATE · only you</Badge>
          )}
        </span>
      }
      actions={canEdit && !editing ? <IconButton icon="Pencil" label={`Edit ${p.ref}`} size="sm" onClick={() => setEditing(true)} /> : null}
    >
      {editing ? (
        <div className="sn-stack">
          <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-3)" }}>
            <TextField label="Reference" placeholder="ref" value={f.ref} onChange={(e) => setF({ ...f, ref: e.target.value })} style={{ width: 160 }} />
            <TextField label="Title" placeholder="title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="flex-1" />
          </div>
          <TextArea label="Prompt body" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={5} />
          <div className="sn-row">
            <Button variant="primary" size="sm" icon="Check" onClick={save}>Save</Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <p className="sn-muted" style={{ margin: 0, fontSize: 13, whiteSpace: "pre-wrap" }}>{p.body}</p>
      )}
    </Panel>
  );
}

export default function Prompts() {
  const { can } = useAuth();
  const canEdit = can("analyst");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ ref: "", title: "", body: "" });
  const { data: rows = [], refetch } = useQuery({ queryKey: ["pm-prompts"], queryFn: api.pmPrompts });

  const add = async () => {
    if (!form.ref.trim() || !form.title.trim()) { toast.error("ref and title required."); return; }
    try { await api.pmAddPrompt(form); setForm({ ref: "", title: "", body: "" }); setAdding(false); refetch(); toast.success("Prompt added."); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="pm-prompts">
      <PageHead
        crumb={["Project", "Prompt library"]}
        title="Prompt library"
        context={<>Maintained prompts to recreate or extend this project (<span className="sn-num">{rows.length}</span>).</>}
        actions={canEdit && <Button variant="primary" icon="Plus" aria-expanded={adding} onClick={() => setAdding((v) => !v)} data-testid="pm-prompt-add-btn">Add prompt</Button>}
      />

      {adding && (
        <Panel title="New prompt" icon="Plus" data-testid="pm-prompt-form">
          <div className="sn-stack">
            <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-3)" }}>
              <TextField label="Reference" placeholder="ref (e.g. backtest)" value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} style={{ width: 192 }} />
              <TextField label="Title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="flex-1" />
            </div>
            <TextArea label="Prompt body" placeholder="Prompt body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} />
            <Button variant="primary" style={{ alignSelf: "flex-start" }} onClick={add} data-testid="pm-prompt-create">Add</Button>
          </div>
        </Panel>
      )}

      {rows.length === 0
        ? <EmptyState icon="FileText" title="No prompts yet">Add the first prompt to the library.</EmptyState>
        : rows.map((p) => <PromptCard key={p.id} p={p} canEdit={canEdit} onSaved={refetch} />)}
    </div>
  );
}
